import React, { useCallback, useMemo, useRef } from "react";

import cn from "classnames";

import { EntityType } from "@mapmycustomers/shared/enum";
import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import User from "@mapmycustomers/shared/types/User";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import ListRequest from "@mapmycustomers/shared/types/viewModel/internalModel/ListRequest";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import ColumnVisibility from "@app/types/viewModel/ColumnVisibility";
import ListFetcher from "@app/types/viewModel/ListFetcher";

import ViewContext from "../utils/ViewContext";

import styles from "./TableListView.module.scss";
import IListViewContext from "./types/IListViewContext";
import ListViewComponent from "./types/ListViewComponent";

interface Props<T> {
  children: React.ReactNode;
  className?: string;
  entityType?: EntityType;
  fieldModel: IFieldModel;
  listFetcher: ListFetcher<T>;
  listViewState: ViewState;
  totalRecords?: number;
}

const TableListView = <T,>({
  children,
  className,
  entityType,
  fieldModel,
  listFetcher,
  listViewState,
  totalRecords,
}: Props<T>) => {
  const components = useRef<Set<ListViewComponent<T>>>(new Set());

  const handleExternalFilterModelChange = useCallback((filterModel: Partial<FilterModel>) => {
    Array.from(components.current).forEach((callback) => {
      callback.onFilterChange?.(filterModel);
    });
  }, []);

  const handleExternalColumnVisibilityChange = useCallback(
    (columnVisibilities: ColumnVisibility[]) => {
      Array.from(components.current).forEach((callback) => {
        callback.onColumnVisibilityChange?.(columnVisibilities);
      });
    },
    []
  );

  const handleUpdateEntity = useCallback((entity: T) => {
    Array.from(components.current).forEach((callback) => {
      callback.onUpdateEntity?.(entity);
    });
  }, []);

  const handleViewAsChange = useCallback(
    (userId: undefined | User["id"]) => {
      listFetcher({ request: { viewAs: userId }, updateOnly: true });
      Array.from(components.current).forEach((callback) => {
        callback.onViewAsChange?.(userId);
      });
    },
    [listFetcher]
  );

  const handleGoToPage = useCallback(
    (page: number, pageSize: number) => {
      const newStartRow = page * pageSize;
      listFetcher({
        request: {
          range: {
            endRow: newStartRow + pageSize,
            startRow: newStartRow,
          },
        },
        updateOnly: false,
      });
    },
    [listFetcher]
  );

  const handleRegister = useCallback(
    (component: ListViewComponent<T>) => {
      components.current.add(component);
    },
    [components]
  );

  const handleUnregister = useCallback(
    (component: ListViewComponent<T>) => {
      components.current.delete(component);
    },
    [components]
  );

  const handleUpdateListViewState = useCallback(
    (request: Partial<ListRequest>) => listFetcher({ request, updateOnly: true }),
    [listFetcher]
  );

  const handleUpdateListViewStateAndFetch = useCallback(
    (
      request: Partial<ListRequest>,
      dataCallback?: (response: ListResponse<T>) => void,
      failCallback?: () => void
    ) => listFetcher({ dataCallback, failCallback, request, updateOnly: false }),
    [listFetcher]
  );

  const context: IListViewContext<T> = useMemo(
    () => ({
      entityType,
      fieldModel,
      goToPage: handleGoToPage,
      register: handleRegister,
      totalRecords,
      unregister: handleUnregister,
      updateColumnVisibility: handleExternalColumnVisibilityChange,
      updateEntity: handleUpdateEntity,
      updateFilterModel: handleExternalFilterModelChange,
      updateViewAs: handleViewAsChange,
      updateViewState: handleUpdateListViewState,
      updateViewStateAndFetch: handleUpdateListViewStateAndFetch,
      viewState: listViewState,
    }),
    [
      entityType,
      fieldModel,
      handleExternalFilterModelChange,
      handleExternalColumnVisibilityChange,
      handleGoToPage,
      handleUpdateEntity,
      handleViewAsChange,
      handleRegister,
      handleUnregister,
      handleUpdateListViewState,
      handleUpdateListViewStateAndFetch,
      listViewState,
      totalRecords,
    ]
  );

  return (
    <section className={cn(styles.container, className)}>
      <ViewContext.Provider
        // Ignored because TS can't be sure that the Provider was created for exactly same
        // entity type as T. Although we know that it will be.
        // @ts-ignore
        value={context}
      >
        {children}
      </ViewContext.Provider>
    </section>
  );
};

export default TableListView;
